import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";

const TIER_ACCENT = {
  starter: {
    border: "border-border",
    badge: "bg-secondary text-foreground",
    button: "",
  },
  professional: {
    border: "border-foreground",
    badge: "bg-foreground text-background",
    button: "",
  },
  team: {
    border: "border-border",
    badge: "bg-secondary text-foreground",
    button: "",
  },
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null); // plan_id being started
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans));
    // Stripe redirects back with ?cancel=1 on abandonment. Show a toast
    // then strip the query so a browser refresh doesn't re-trigger it and
    // a stale bookmark doesn't dead-end the user on Stripe's error page.
    if (params.get("cancel")) {
      toast.info("Checkout cancelled — pick a plan below to try again.");
      navigate("/pricing", { replace: true });
    }
    if (params.get("error")) {
      toast.error(
        "That checkout link expired or was already used. Pick a plan to start a fresh one."
      );
      navigate("/pricing", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed — try again");
      setBusy(null);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="pricing-page">
      <div className="reveal text-center">
        <span className="app-eyebrow">/ Plans</span>
        <h1 className="font-display text-4xl md:text-6xl mt-3">
          Close more deals.
          <br />
          <span style={{ color: "var(--app-accent)" }}>Pay less than one wholesale fee.</span>
        </h1>
        <p className="text-[var(--app-text-muted)] mt-4 max-w-2xl mx-auto">
          A single FlipPilot plan costs less than one JV split. Cancel anytime.
          Prices are monthly USD.
        </p>
      </div>

      {user?.subscription_tier && (
        <div className="mt-8 card-flat p-4 flex items-center justify-between reveal reveal-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--app-text-muted)]">
              Current plan
            </div>
            <div className="font-medium capitalize mt-0.5">
              {user.subscription_tier}
              {user.subscription_expires_at && (
                <span className="text-[var(--app-text-muted)] text-sm ml-2">
                  · renews {new Date(user.subscription_expires_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <Sparkles size={16} className="text-[var(--app-accent)]" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
        {plans.map((p, i) => {
          const acc = TIER_ACCENT[p.id] || TIER_ACCENT.starter;
          const isFeatured = p.id === "professional";
          const isCurrent = user?.subscription_tier === p.id;
          return (
            <div
              key={p.id}
              className={`card-flat p-6 border-2 ${acc.border} relative reveal reveal-${i + 2}`}
              data-testid={`plan-card-${p.id}`}
            >
              {isFeatured && (
                <div className="absolute -top-3 left-6 bg-[var(--app-accent)] text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--app-text-muted)]">
                {p.name}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="kpi-num text-5xl">{money(p.amount)}</div>
                <div className="text-sm text-[var(--app-text-muted)]">/mo</div>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check
                      size={14}
                      className="text-[hsl(var(--success))] mt-1 shrink-0"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`checkout-${p.id}`}
                disabled={busy === p.id || isCurrent}
                onClick={() => startCheckout(p.id)}
                className={`w-full mt-6 rounded-full h-11 ${
                  isFeatured
                    ? "bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                    : "bg-[var(--app-surface)] text-[var(--app-text)] hover:bg-[var(--app-surface-2)] border border-[var(--app-line)]"
                }`}
              >
                {isCurrent ? (
                  "Current plan"
                ) : busy === p.id ? (
                  <><Loader2 className="animate-spin mr-2" size={14} />Redirecting…</>
                ) : (
                  `Start ${p.name}`
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center text-xs text-muted-foreground">
        Powered by Stripe · Test mode · Secure checkout
      </div>
    </div>
  );
}
